import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "../auth";
import { useDebounce } from "../shared/useDebounce";

export interface CompanySearchResult {
  id: string;
  name: string;
  website: string | null;
  industry: string | null;
}

export function useCompanySearch() {
  const { orgId } = useCurrentOrgId();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["company-search", orgId, debouncedQuery],
    queryFn: async () => {
      if (!orgId) return [];

      let q = supabase
        .from("companies")
        .select("id, name, website, industry")
        .eq("org_id", orgId);

      if (debouncedQuery.length >= 2) {
        q = q.ilike("name", `%${debouncedQuery}%`).limit(10);
      } else {
        q = q.order("updated_at", { ascending: false }).limit(5);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as CompanySearchResult[];
    },
    enabled: !!orgId,
    staleTime: 10_000,
  });

  return { companies, isLoading, query, setQuery };
}

/** Busca uma empresa por ID (para pré-preencher seleção default). */
export function useCompanyById(companyId: string | undefined) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ["company-by-id", orgId, companyId],
    queryFn: async () => {
      if (!orgId || !companyId) return null;

      const { data, error } = await supabase
        .from("companies")
        .select("id, name, website, industry")
        .eq("id", companyId)
        .eq("org_id", orgId)
        .single();

      if (error) throw error;
      return data as CompanySearchResult;
    },
    enabled: !!orgId && !!companyId,
    staleTime: 60_000,
  });
}
